import { getFocusGetConversionInfo } from '@apis/Invoices';
import { ConversionPlanBundle, FocusFieldInfo } from '@apis/Invoices/model';
import { singleton } from 'tsyringe';
import { Platform } from '../PlatformService';

const schemaPlatformLookup = new Map<string, Platform>([
    ['AwsCurV1', 'Aws'],
    ['AwsDataExportsCurV2', 'Aws'],
    ['AzureCostExportIdeal20240101', 'Azure'],
]);

export class FocusMetadataInfo {
    private readonly focusFieldByField = new Map<string, FocusFieldInfo>();
    private readonly focusFieldByAlias = new Map<string, FocusFieldInfo>();
    private readonly cspFields = new Map<Platform, { cspAlias: string; field: FocusFieldInfo }[]>();
    private readonly cspAliasLookup = new Map<string, string>();
    private readonly ignoredFields = new Map<string, FocusFieldInfo>();
    public static focusLabel = 'FOCUS™';

    public constructor(public readonly focusMeta: ConversionPlanBundle) {
        for (const field of focusMeta.FocusFields ?? []) {
            if (!field.Ignore) {
                this.focusFieldByField.set(field.Field ?? '', field);
                for (const alias of field.Aliases ?? []) {
                    const platform = schemaPlatformLookup.get(alias.Schema ?? '');
                    if (platform && alias.Field) {
                        this.focusFieldByAlias.set(this.getCspFieldKey(platform, alias.Field), field);
                    }
                }
            } else {
                this.ignoredFields.set(field.Field ?? '', field);
            }
        }
    }

    public getCspAliasedFields(platform: Platform) {
        if (!this.cspFields.size) {
            this.createCspFieldLookup();
        }
        return this.cspFields.get(platform);
    }
    public getCspAlias(platform: Platform, focusField: string) {
        if (!this.cspAliasLookup.size) {
            this.createCspFieldLookup();
        }
        return this.cspAliasLookup.get(this.getCspAliasKey(platform, focusField));
    }
    public getFieldByAlias(platform: Platform, cspField: string): FocusFieldInfo | undefined {
        return this.focusFieldByAlias.get(this.getCspAliasKey(platform, cspField));
    }
    public getField(field: string, includeIgnored?: boolean) {
        return this.focusFieldByField.get(field) ?? (includeIgnored ? this.ignoredFields.get(field) : undefined);
    }
    public getFields() {
        return this.focusMeta.FocusFields ?? [];
    }

    private createCspFieldLookup() {
        for (const field of this.focusMeta.FocusFields ?? []) {
            for (const alias of field.Aliases ?? []) {
                const platform = schemaPlatformLookup.get(alias.Schema ?? '');
                if (platform && !field.Ignore && alias.Field !== field.Field && !!alias.Field && !!field.Field) {
                    const cspFields = this.cspFields.get(platform) ?? [];
                    cspFields.push({ cspAlias: alias.Field, field });

                    this.cspFields.set(platform, cspFields);
                    this.cspAliasLookup.set(this.getCspAliasKey(platform, field.Field), alias.Field);
                }
            }
        }
    }

    private getCspAliasKey(platform: Platform, focusField: string) {
        return `${platform}-${focusField}`;
    }

    private getCspFieldKey(platform: Platform, cspField: string) {
        return `${platform}-${cspField}`;
    }
}

@singleton()
export class FocusMetadataService {
    private _focusMetadata?: Promise<FocusMetadataInfo>;

    public constructor() {}

    public getFocusMetadata(): Promise<FocusMetadataInfo> {
        return (this._focusMetadata ??= this.loadFocusMetadata());
    }

    private async loadFocusMetadata() {
        const conversionBundle = await getFocusGetConversionInfo();
        return new FocusMetadataInfo(conversionBundle);
    }
}
